export const en = {
  app: {
    product: "HAUFFE OS",
    workspace: "Customer Value Workspace",
    subline: "Guided Digital Access"
  },
  layout: {
    languageLabel: "Language",
    nav: {
      overview: "Overview",
      workspaces: "Workspaces",
      newWorkspace: "New Workspace",
      users: "Users",
      workspace: "Workspace",
      departments: "Departments",
      evaluation: "HAUFFE OS Evaluation",
      customerValue: "Customer Value Calculation",
      myPlans: "My Plans",
      dealWinEngine: "Deal Win Engine",
      contributor: "My Department",
      entry: "Entry",
      pricing: "Pricing",
      login: "Login",
      logout: "Logout"
    },
    noAccess: {
      eyebrow: "No access",
      title: "You do not have permission to view this page.",
      description: "Open the view assigned to your access.",
      cta: "Back to my view"
    },
    loginRequired: {
      eyebrow: "Login required",
      title: "Please log in",
      description: "Log in to open your Customer Value Workspace.",
      cta: "Go to login"
    }
  },
  home: {
    eyebrow: "Guided Digital Access",
    title: "HAUFFE OS Customer Value Workspace",
    description: "A digital decision space for organizations that want to evaluate customer value, customer segments and new customer potential through one shared logic.",
    cta: "Open Workspace",
    pricingCta: "View pricing",
    cards: [
      {
        title: "Make department perspectives visible",
        body: "Sales, Marketing, Delivery and Finance can work separately without their perspectives being blurred together."
      },
      {
        title: "Prepare shared Customer Value logic",
        body: "Departments enter their view separately. From this, a shared evaluation logic for customers, leads and opportunities emerges."
      },
      {
        title: "The decision remains with the company",
        body: "The app does not evaluate people. It does not take the decision away; it removes the uncertainty before it."
      }
    ]
  },
  login: {
    title: "Login",
    description: "Log in to open your Customer Value Workspace.",
    panelTitle: "Prepare access",
    formLabel: "Login form",
    email: "Email address",
    password: "Password",
    passwordPlaceholder: "Your password",
    note: "Workspace access is set up in a controlled way.",
    resetNote: "Password reset is not yet automatically enabled for this workspace. Please contact HAUFFE. HAUFFE admin passwords are currently set through the secure admin set script.",
    demoActive: "Demo access active.",
    submit: "Log in"
  },
  setupPassword: {
    title: "Set password",
    description: "Set your password to open the HAUFFE OS Customer Value Workspace.",
    readyTitle: "Access is ready",
    newPasswordTitle: "New password",
    readyBody: "Your password has been set. You can now log in.",
    loginCta: "Go to login",
    missingToken: "The password link is missing or incomplete.",
    mismatch: "The passwords do not match.",
    password: "Password",
    confirmPassword: "Confirm password",
    note: "This link can be used once and is valid for 24 hours.",
    submit: "Save password"
  },
  setupAccount: {
    title: "Create account",
    description: "Create your HAUFFE OS account after confirmed payment.",
    panelTitle: "Set up account",
    readyTitle: "Workspace is ready",
    readyBody: "Your account has been created. The workspace is now available.",
    openWorkspace: "Open Workspace",
    missingToken: "The account link is missing or incomplete.",
    mismatch: "The passwords do not match.",
    error: "The account could not be created.",
    name: "Name optional",
    password: "Password",
    confirmPassword: "Confirm password",
    note: "This link can be used once. Access is activated only after confirmed payment and account setup.",
    submit: "Create account"
  },
  pricing: {
    eyebrow: "Buy HAUFFE OS",
    title: "Hauffe OS Price List",
    description: "Choose Guided Digital Access, Deal Win Engine, or the right combination. All prices are net prices for B2B customers.",
    guidedSectionTitle: "Guided Digital Access for Customer Value",
    dealWinSectionTitle: "Deal Win Engine",
    executiveIncluded: "Executive Management always included – does not count as a department.",
    includedUsers: "included users",
    extraUsersAvailable: "Additional users can be added flexibly",
    dealWinSingleBadge: "Focused start",
    dealWinBundleBadge: "Team advantage",
    dealWinSelect: "Choose this package",
    bundleDiscountHint: "Combine Team 3, 5, or 10 with Guided Digital Access and secure a 10% Guided advantage.",
    bundleDiscountActive: "10% bundle advantage on Guided Digital Access is planned.",
    bundleCheckoutPrepared: "10% bundle advantage on Guided Digital Access is planned. Bundle checkout is being prepared.",
    dealWinCheckoutPrepared: "After purchase, the Account Owner manages access and assigns licenses internally.",
    guidedOnlyCheckoutNote: "Currently, only Guided Digital Access is booked through this checkout. Deal Win Engine checkout starts separately.",
    separateCheckoutNote: "Guided Digital Access and Deal Win Engine are currently booked through separate checkouts.",
    loadError: "Pricing could not be loaded.",
    checkoutError: "Checkout could not be started.",
    successTitle: "Payment is being verified",
    successBody: "Thank you. Once Stripe confirms the payment, HAUFFE OS sends the account link by email.",
    successNote: "The success page does not activate access. The workspace is activated only after verified webhook and account setup.",
    cancelledTitle: "Checkout cancelled",
    cancelledBody: "The purchase was not completed. You can select a plan again at any time.",
    formTitle: "Book plan",
    summaryTitle: "Your selection",
    summaryEmpty: "Select a plan to see your summary.",
    selectedPlan: "Selected plan",
    duration: "Duration",
    basePrice: "Base price",
    addonPrice: "Additional departments",
    discount15: "Discount 15%",
    upgradeDiscount15: "Upgrade with 15% discount",
    renewalDiscount15: "Renewal with 15% discount",
    totalPrice: "Total price",
    vatNote: "plus applicable VAT",
    discountError: "The discount could not be applied. Please contact support.",
    stripeNote: "Payment via Stripe",
    noExtraDepartments: "No additional departments",
    email: "Email address",
    company: "Company",
    name: "Name optional",
    extraDepartments: "Additional departments",
    extraDepartmentsProduct: "Additional departments (Guided Digital Access)",
    legalNote: "Checkout starts a secure Stripe payment. Productive access is created only after payment confirmation and account setup.",
    buyNow: "Buy selection",
    dealWinBuyNow: "Buy Deal Win Engine",
    checkoutPrepared: "Checkout is being prepared",
    extraDealWinUsers: "Additional Deal Win users",
    extraDealWinUsersProduct: "Additional users (Deal Win Engine)",
    totalDealWinUsers: "Total Deal Win users",
    dealWinTotalPrice: "Deal Win Engine total",
    existingCustomer: "I already have access",
    includedDepartments: "departments included",
    extraDepartmentOnce: "net per additional department",
    extraDepartmentMonthly: "net monthly per additional department",
    months: "months",
    monthlyNotice: "Monthly subscription with cancellation before the next billing period",
    perMonth: "net / month",
    net: "net",
    selectPlan: "Select plan",
    selected: "Selected"
  },
  workspace: {
    login: "Login",
    setupPassword: "Set password",
    contributorWorkspace: "Contributor Workspace",
    ownerWorkspace: "Company Owner Workspace",
    evaluation: "HAUFFE OS Evaluation",
    department: "Department",
    departmentPerspective: "Department Perspective",
    valueRound: "Value Round",
    identifyCriteria: "Identify Customer Value Criteria",
    clusterCriteria: "Cluster Criteria",
    finalizeWeighting: "Finalize Weighting",
    customerValueCalculation: "Customer Value Calculation",
    newCustomerValueCalculation: "New Customer Value Calculation",
    scoringLogic: "Scoring Logic",
    numericRange: "Numeric Range",
    unit: "Unit",
    scoringScale: "1 to 10 scoring scale"
  },
  contributorDepartment: {
    assignedOnlyTitle: "Perspective is assigned",
    assignedOnlyBody: "This perspective is assigned to the invited contributor. Please let that person submit their perspective themselves.",
    assignedOnlyError: "This perspective can only be submitted by the assigned person.",
    valueRoundOpensAfterEvaluation: "The Value Round opens after the HAUFFE OS Evaluation of the first perspectives."
  },
  pdf: {
    title: "HAUFFE OS Evaluation",
    nextStep: "Next step: Identify Customer Value Criteria",
    footer: "HAUFFE OS Customer Value Workspace"
  },
  email: {
    valueRoundOpened: "New Value Round in the HAUFFE OS Workspace",
    valueRoundCompleted: "All Value Perspectives have been submitted",
    calculationReady: "Customer Value Calculation ready for review",
    calculationConfirmed: "Customer Value Calculation confirmed"
  },
  dealWinEngine: {
    eyebrow: "Workspace Module",
    title: "Deal Win Engine",
    description: "Evaluate concrete opportunities, detect risks and see which levers can increase win probability.",
    overview: "Deals",
    newDeal: "Create new deal",
    empty: "No deals created yet. Create your first deal to evaluate win probability.",
    loading: "Deal Win Engine is loading...",
    loadError: "Deal Win Engine data could not be loaded.",
    open: "Open",
    archive: "Archive deal",
    archiveConfirm: "This deal will be archived and hidden from the normal overview. Historical assessments are preserved.",
    edit: "Edit deal",
    save: "Save deal",
    cancel: "Cancel",
    assess: "Evaluate deal",
    reassessNote: "Evaluate the deal again to update the score after changes.",
    latestAssessment: "Current deal review",
    noAssessment: "No evaluation exists for this deal yet.",
    noAssessmentGuidance: "This deal has not been evaluated yet. Start the first evaluation to see win probability, risks and improvement levers.",
    noAssessmentDetail: "No assessment exists yet. Capture the deal data and start the first deal review.",
    assessNow: "Evaluate deal now",
    scoreBreakdown: "Score breakdown",
    winProbability: "Win probability",
    dealScore: "Deal quality score",
    dealValueLabel: "Deal value",
    expectedSigning: "Expected signing",
    dealStatus: "Deal status",
    currentDealQuality: "Current deal quality",
    dealValueMeaning: "Commercial value of the opportunity",
    winProbabilityMeaning: "Estimated win probability, not a commitment",
    expectedSigningMeaning: "Current target date for signing",
    statusMeaning: "Current process status",
    currentDealQualityMeaning: "Maturity and controllability based on known criteria",
    biggestRisk: "Biggest risk",
    strongestLever: "Strongest next lever",
    lastAssessed: "Last assessed",
    reviewSummary: "Executive view of deal quality, risk and next step.",
    riskLevel: "Risk level",
    confidence: "Confidence",
    assessmentDate: "Evaluation date",
    assessmentVersion: "Evaluation version",
    topLevers: "Top improvement levers",
    whatIf: "What-if simulation",
    nextBestAction: "Next best action",
    simulatedTarget: "Simulated target value",
    selectedLevers: "Selected levers",
    selectedScoreImpact: "Modeled score effect",
    selectedProbabilityImpact: "Modeled change in win probability",
    noSelectedLevers: "Select one or more levers to see their combined effect.",
    simulationNote: "Simulation based on estimated lever impact. Individual effects must not be treated as guaranteed percentage points; the actual evaluation is created only after re-evaluating the deal.",
    scoreFormulaNote: "The deal quality score is the sum of the weighted contributions across all dimensions.",
    scoreContribution: "Contribution",
    scoreRating: "Rating",
    scoreWeight: "Weighting",
    whatIfScoreGuaranteeNote: "100 points indicate maximum deal quality in the model, not a 100% win guarantee.",
    whatIfNonAdditiveNote: "Levers show modeled effects on deal quality and controllability. They are not an automatic addition to win probability.",
    contactProfileQualitativeNote: "Contact risk drivers improve conversation guidance, risks and next steps. They do not automatically change the deal quality score.",
    whatIfScoreCapReached: "Based on the known criteria, this deal is very well secured. Further structural levers are currently limited; Sales should hold momentum, secure decision steps and keep risks under active observation.",
    whatIfBelowMaxNote: "The selected levers can make the deal more controllable. 100 points are reached only when all relevant criteria are robustly fulfilled.",
    whatIfSingleLeverNote: "The most important structural lever is currently the visible selected lever. Other factors are already sufficiently evidenced or cannot be improved reliably from the available data.",
    updateAndReassess: "Update deal data and evaluate again",
    noNextAction: "No critical next action was detected for this evaluation.",
    risks: "Risks",
    actions: "Recommended next steps",
    levers: "All improvement levers",
    noRisks: "No material risks detected.",
    crmExport: "CRM export",
    crmExportIntro: "These values can be copied into custom fields in HubSpot, Pipedrive, Salesforce or another CRM.",
    crmFieldList: "Structured field list",
    crmJson: "Technical JSON export is available for CRM field setup and can be copied.",
    crmNote: "CRM note",
    customerValueContextUsed: "Customer value included in deal assessment",
    customerValueContextMissing: "No customer value context linked yet",
    customerValueContextMissingDescription: "No customer value context linked yet. This assessment currently relies on opportunity data only.",
    importCustomerValueSnapshot: "Include customer value in deal assessment",
    updateCustomerValueSnapshot: "Update customer value context",
    customerValueSnapshotSuccess: "Customer value context was included in the deal assessment.",
    customerValueSnapshotError: "Customer value context could not be included in the deal assessment.",
    customerValueSnapshotNote: "A concrete customer value segment is included only when this deal already has a customer value score.",
    customerValueContextStatus: "Customer value status",
    customerValueModelOnly: "The customer value model is linked. This specific deal does not yet have a customer value score.",
    customerValueConcreteSegment: "This deal is assessed with a specific customer value score.",
    customerValueCriteriaCount: "Active criteria",
    customerValueScore: "Customer Value score",
    customerValueScoreHelp: "Optional score from 0 to 100. Use it when customer value is already known from Hauffe OS, CRM, or an assessment.",
    customerFitScore: "Fit with the ideal customer profile",
    customerValueCommercialFit: "Commercial deal quality",
    customerValueSegment: "Customer Value segment",
    customerValueFitCategory: "Fit with the ideal customer profile",
    customerValueAssessedAt: "Customer Value evaluation date",
    contactProfileTitle: "Contact decision profile",
    contactProfileDescription: "This profile is a working hypothesis for sales conversation and deal steering. It does not replace personal judgement.",
    contactProfileSummary: "Contact profile available for conversation planning",
    contactProfileMissing: "No contact decision profile has been added yet.",
    contactProfileSave: "Save contact profile",
    contactProfileHypothesis: "Working hypothesis for the sales conversation",
    contactProfileHelp: "Use this profile when decision style, proof needs or risk drivers are visible in the conversation.",
    contactProfileReviewHint: "Note from contact profile",
    proofStatusTitle: "Proof maturity",
    proofStatusDescription: "Assess only the proof points that matter for this contact. Missing proof can influence risks and next steps.",
    nextStepQualityTitle: "Next-step quality",
    contactProfileSectionInstructions: {
      riskTriggers: "Select the risk drivers that could slow this contact down in the decision process.",
      proofPreference: "Select the proof points that would make the decision more robust for this contact.",
      recommendedApproach: "Select the conversation approach Sales should use to lead the next step."
    },
    contactProfileSelected: "Selected",
    contactProfileLabels: {
      source: "Source",
      confidence: "Confidence in the hypothesis",
      decisionStyle: "Decision style",
      communicationPreference: "Communication preference",
      riskTriggers: "Risk drivers",
      proofPreference: "Preferred proof",
      recommendedApproach: "Recommended conversation approach",
      notes: "Conversation note"
    },
    contactProfileSources: {
      manual: "Entered manually",
      crm: "Mapped from CRM fields",
      inferred: "Inferred from available deal information"
    },
    contactProfileDecisionStyles: {
      analytical: "Numbers and facts oriented",
      relationship_driven: "Trust and relationship oriented",
      authority_driven: "Authority and hierarchy oriented",
      innovation_driven: "Innovation and future oriented",
      risk_averse: "Risk and security oriented",
      speed_oriented: "Speed and execution oriented",
      consensus_driven: "Consensus and stakeholder oriented"
    },
    contactProfileCommunicationPreferences: {
      numbers_and_facts: "Numbers, facts and decision basis",
      strategic_vision: "Strategic vision",
      operational_detail: "Operational detail and execution",
      trust_and_relationship: "Trust and relationship",
      proof_and_references: "Proof and references",
      concise_executive_summary: "Concise executive summary"
    },
    contactProfileRiskTriggers: {
      unclear_roi: "Unclear ROI",
      implementation_effort: "Implementation effort",
      internal_politics: "Internal politics",
      budget_uncertainty: "Budget uncertainty",
      loss_of_control: "Loss of control",
      reputational_risk: "Reputational risk",
      missing_references: "Missing references",
      unclear_decision_path: "Unclear decision path"
    },
    contactProfileRiskTriggerDescriptions: {
      unclear_roi: "The economic value is not robust enough yet for a confident internal decision.",
      implementation_effort: "The contact may weigh effort, resources or internal burden higher than value.",
      internal_politics: "Multiple interests or hidden blockers may slow the close.",
      budget_uncertainty: "Budget logic or willingness to pay is not decision-ready yet.",
      loss_of_control: "The contact may fear losing control over process, data or decision.",
      reputational_risk: "The decision may be visible internally and carry personal or functional risk.",
      missing_references: "Comparable proof from similar situations is still missing.",
      unclear_decision_path: "It is not clear yet who decides and which steps are required before that."
    },
    contactProfileProofPreferences: {
      business_case: "Business case",
      customer_reference: "Customer reference",
      roi_calculation: "ROI calculation",
      implementation_plan: "Implementation plan",
      executive_summary: "Executive summary",
      technical_validation: "Functional or technical validation",
      pilot_or_test: "Pilot or test"
    },
    contactProfileProofPreferenceDescriptions: {
      business_case: "Numbers-based decision basis with value, effort and commercial effect.",
      customer_reference: "Proof from a comparable customer or similar starting point.",
      roi_calculation: "Concrete economic logic instead of generic value arguments.",
      implementation_plan: "Confidence through clear steps, responsibilities and effort transparency.",
      executive_summary: "Condensed decision document for management and the buying center.",
      technical_validation: "Functional or technical validation of feasibility.",
      pilot_or_test: "Low-risk validation before a larger decision is made."
    },
    proofStatusLabels: {
      missing: "Missing",
      partial: "Partially available",
      provided: "Provided",
      strong: "Strong",
      similar: "Similar reference",
      matching: "Matching reference",
      planned: "Planned",
      running: "Running",
      completed: "Completed"
    },
    proofStatusHelp: {
      business_case: "This contact needs an economic decision basis.",
      customer_reference: "This contact needs credible proof from a comparable situation.",
      roi_calculation: "This contact needs a traceable ROI logic.",
      implementation_plan: "This contact needs clarity on implementation.",
      executive_summary: "This contact needs a decision-ready executive summary.",
      technical_validation: "This contact needs robust technical or functional validation.",
      pilot_or_test: "This contact needs low-risk validation before a larger decision."
    },
    nextStepQualityLabels: {
      none: "No next step",
      vague: "Vague callback",
      follow_up: "Follow-up without decision objective",
      scheduled: "Scheduled next step",
      decision_meeting: "Decision meeting",
      executive_decision: "Executive decision step"
    },
    contactProfileRecommendedApproaches: {
      lead_with_business_case: "Lead with business case",
      reduce_perceived_risk: "Reduce perceived risk",
      secure_internal_sponsor: "Secure internal sponsor",
      show_reference_or_proof: "Show reference or proof",
      clarify_decision_path: "Clarify decision path",
      create_executive_summary: "Create executive summary",
      align_on_next_decision: "Align on next decision"
    },
    contactProfileRecommendedApproachDescriptions: {
      lead_with_business_case: "Steer the next step through commercial value, decision logic and priority.",
      reduce_perceived_risk: "Pre-empt objections and create confidence through reference, plan or pilot.",
      secure_internal_sponsor: "Activate someone in the buying center who can carry the need internally.",
      show_reference_or_proof: "Use credible proof to build confidence in decision and implementation.",
      clarify_decision_path: "Clarify who decides, who influences and which decision is needed next.",
      create_executive_summary: "Prepare a concise decision document for management or the buying center.",
      align_on_next_decision: "Agree the next concrete decision with the customer."
    },
    decisionMakerAccessOptions: {
      yes: "Yes",
      no: "No",
      unknown: "Unknown"
    },
    formSections: {
      dealContext: "Deal context",
      decisionQuality: "Decision quality",
      customerValueFit: "Customer value & fit"
    },
    copyJson: "Copy JSON",
    copyNote: "Copy CRM note",
    copied: "Copied",
    copyUnavailable: "Copying is not available in this browser. The text remains visible and can be copied manually.",
    noCrmSync: "No automatic CRM synchronization. These values are shown only for manual copying.",
    history: "Recent evaluations",
    back: "Back to Deal Win Engine",
    details: "Deal details",
    formTitle: "Deal data",
    createSuccess: "Deal was created.",
    updateSuccess: "Deal was updated.",
    archiveSuccess: "Deal was archived.",
    assessSuccess: "Deal was evaluated.",
    fields: {
      title: "Deal title",
      companyName: "Company",
      dealValue: "Deal value",
      currency: "Currency",
      salesStage: "Sales stage",
      expectedCloseDate: "Expected close date",
      ownerName: "Owner",
      status: "Status",
      contactName: "Contact name",
      contactEmail: "Contact email",
      industry: "Industry",
      companySize: "Company size",
      decisionMakerAccess: "Decision-maker access",
      buyingCenterKnown: "Buying center known",
      budgetConfirmed: "Budget confirmed",
      painConfirmed: "Pain confirmed",
      urgencyLevel: "Urgency",
      competitorKnown: "Competition known",
      offerSentAt: "Offer sent at",
      lastActivityAt: "Last activity",
      customerValueScore: "Customer value score for this deal",
      nextStep: "Next step",
      notes: "Notes"
    },
    yes: "Yes",
    no: "No",
    unknown: "Unknown"
  }
} as const;
